/**
 * Sign-in effects — async side effects triggered by command actions.
 *
 * Each handler:
 *   1. Guards on the expected action type (returns early if wrong)
 *   2. Performs async work
 *   3. Dispatches exactly one result action
 *
 * Errors thrown here are caught by FlowStore's mandatory catch,
 * which calls signInDeriveFailure and dispatches the typed failure action.
 */

import type { EffectHandler } from '../../shared/flow-store';
import type { SignInState, SignInAction, SignInDeps } from './types';
import { extractErrorMessage } from '../../shared/extract-error-message';

type SignInEffectHandler = EffectHandler<SignInState, SignInAction, SignInDeps>;
type Dispatch = (a: SignInAction) => void;

// ─── Effect handlers ─────────────────────────────────────────────────────────

async function submitEmailEffect(
  action: SignInAction,
  _prevState: SignInState,
  dispatch: Dispatch,
  deps: SignInDeps,
): Promise<void> {
  if (action.type !== 'SUBMIT_EMAIL') return;
  const { identifier: email } = action;

  try {
    const [clientApp, cognitoCode] = await Promise.all([
      deps.service.checkClientAppUser(email),
      deps.service.checkCognitoUser(email),
    ]);

    // Auto-resend verification code for unconfirmed accounts
    if (cognitoCode === 'UserNotConfirmedException') {
      deps.service.resendSignUpCode(email).catch(err => {
        deps.observability.dispatch({ type: 'resend_code_failure', error: err, email });
      });
    }

    dispatch({ type: 'EMAIL_LOOKUP_OK', cognitoCode, clientApp });
  } catch (err) {
    deps.observability.dispatch({ type: 'email_lookup_failure', error: err });
    dispatch({ type: 'EMAIL_LOOKUP_FAIL', error: toMessage(err) });
  }
}

async function submitPasswordEffect(
  action: SignInAction,
  prevState: SignInState,
  dispatch: Dispatch,
  deps: SignInDeps,
): Promise<void> {
  if (action.type !== 'SUBMIT_PASSWORD') return;
  const identifier = identifierOf(prevState);
  if (!identifier) return;
  try {
    const result = await deps.service.signIn(identifier, action.password);

    if ('needsPhone' in result && result.needsPhone) {
      handleNeedsPhone(dispatch, deps);
      return;
    }

    if (!result.user) {
      throw new Error('Sign-in succeeded but no session was returned. Please try again.');
    }

    deps.session.completeAuth(result.user);
    deps.observability.dispatch({ type: 'signin_success' });
    deps.eventBus.emit({ type: 'email_sign_in_complete', user: result.user });
    dispatch({ type: 'SIGN_IN_OK', user: result.user });
  } catch (err) {
    deps.observability.dispatch({ type: 'signin_failure', error: err });
    const stepOverride = classifySignInError(err);
    if (stepOverride === 'UNCONFIRMED') {
      deps.service.resendSignUpCode(identifier).catch(resendErr => {
        deps.observability.dispatch({ type: 'resend_code_failure', error: resendErr, email: identifier });
      });
    }
    dispatch({ type: 'SIGN_IN_FAIL', stepOverride: stepOverride ?? undefined, error: toMessage(err) });
  }
}

async function submitResetPasswordEffect(
  action: SignInAction,
  prevState: SignInState,
  dispatch: Dispatch,
  deps: SignInDeps,
): Promise<void> {
  if (action.type !== 'SUBMIT_RESET_PASSWORD') return;
  const identifier = identifierOf(prevState);
  if (!identifier) return;

  try {
    const result = await deps.service.forgotPassword(identifier, '');
    if (result.error) {
      deps.observability.dispatch({ type: 'signin_failure', error: new Error(result.error) });
      dispatch({ type: 'RESET_CODE_FAIL', error: result.error });
      return;
    }
    dispatch({ type: 'RESET_CODE_OK' });
  } catch (err) {
    deps.observability.dispatch({ type: 'signin_failure', error: err });
    dispatch({ type: 'RESET_CODE_FAIL', error: toMessage(err) });
  }
}

async function submitVerificationEffect(
  action: SignInAction,
  prevState: SignInState,
  dispatch: Dispatch,
  deps: SignInDeps,
): Promise<void> {
  if (action.type !== 'SUBMIT_VERIFICATION') return;
  const identifier = identifierOf(prevState);
  if (!identifier) return;

  try {
    if (prevState.step === 'UNCONFIRMED') {
      await verifySignUpCode(identifier, action.code, dispatch, deps);
    } else if (prevState.step === 'RESET_CODE_SENT') {
      await verifyPasswordReset(identifier, action.code, prevState.pendingNewPassword, dispatch, deps);
    }
  } catch (err) {
    deps.observability.dispatch({ type: 'signin_failure', error: err });
    dispatch({ type: 'VERIFICATION_FAIL', error: toMessage(err, 'Verification failed.') });
  }
}

async function resendCodeEffect(
  action: SignInAction,
  prevState: SignInState,
  dispatch: Dispatch,
  deps: SignInDeps,
): Promise<void> {
  if (action.type !== 'RESEND_CODE') return;
  const identifier = identifierOf(prevState);
  if (!identifier) return;

  try {
    await deps.service.resendSignUpCode(identifier);
    dispatch({ type: 'RESEND_CODE_OK' });
  } catch (err) {
    deps.observability.dispatch({ type: 'resend_code_failure', error: err, email: identifier });
    dispatch({ type: 'RESEND_CODE_FAIL', error: toMessage(err, 'Failed to resend code.') });
  }
}

export const signInEffects: Partial<Record<SignInAction['type'], SignInEffectHandler>> = {
  SUBMIT_EMAIL: submitEmailEffect,
  SUBMIT_PASSWORD: submitPasswordEffect,
  SUBMIT_RESET_PASSWORD: submitResetPasswordEffect,
  SUBMIT_VERIFICATION: submitVerificationEffect,
  RESEND_CODE: resendCodeEffect,
};

// ─── Sub-handlers ─────────────────────────────────────────────────────────────

/**
 * Verifies a sign-up confirmation code and completes auth on success.
 * If the confirmation succeeds but no session is returned (e.g. the user
 * signed up in a previous session and `pendingProps` is gone), dispatches
 * SIGN_UP_CONFIRMED to route the user to the password step so they can
 * complete signIn -> tokenExchange.
 * Throws on service failure so the caller's catch block dispatches VERIFICATION_FAIL.
 */
async function verifySignUpCode(
  identifier: string,
  code: string,
  dispatch: Dispatch,
  deps: SignInDeps,
): Promise<void> {
  const result = await deps.service.confirmSignUp(identifier, code);
  if (!('user' in result) || !result.user) {
    dispatch({ type: 'SIGN_UP_CONFIRMED' });
    return;
  }
  deps.session.completeAuth(result.user);
  deps.observability.dispatch({ type: 'signin_success' });
  dispatch({ type: 'VERIFICATION_OK', user: result.user });
}

/**
 * Confirms a password reset then immediately signs the user in.
 * Throws on failure so the caller's catch block dispatches VERIFICATION_FAIL.
 *
 * Migrated users (who have never registered a phone number in Rails) will get
 * a `needsPhone: true` result from the token exchange. We announce this via the
 * `needs_phone` event so the app layer can transition to phone collection without
 * any page navigation. The service has already stashed the id-token internally,
 * so a subsequent `submitPhone` call will complete the Rails registration.
 */
async function verifyPasswordReset(
  identifier: string,
  code: string,
  newPassword: string,
  dispatch: Dispatch,
  deps: SignInDeps,
): Promise<void> {
  const confirmResult = await deps.service.confirmForgotPassword(identifier, code, newPassword, '');
  if (confirmResult.error) throw new Error(confirmResult.error);

  const signInResult = await deps.service.signIn(identifier, newPassword);

  if ('needsPhone' in signInResult && signInResult.needsPhone) {
    handleNeedsPhone(dispatch, deps);
    return;
  }

  if (!signInResult.user) {
    throw new Error('Password reset succeeded but sign-in failed. Please try signing in again.');
  }

  deps.session.completeAuth(signInResult.user);
  deps.observability.dispatch({ type: 'signin_success' });
  deps.eventBus.emit({ type: 'migrated_reset_complete', user: signInResult.user });
  dispatch({ type: 'VERIFICATION_OK', user: signInResult.user });
}

/**
 * Single handler for any token exchange that returns `needsPhone: true`.
 *
 * Emits `needs_phone` so the app layer can react (open the phone collection
 * form in-place). No page navigation or sessionStorage persistence is needed
 * for synchronous flows — the IdP session and the service's stashed id-token
 * are already in memory.
 */
function handleNeedsPhone(dispatch: Dispatch, deps: SignInDeps): void {
  deps.eventBus.emit({ type: 'needs_phone' });
  dispatch({ type: 'SIGN_IN_NEEDS_PHONE', user: null });
}

// ─── Utilities ────────────────────────────────────────────────────────────────

export function identifierOf(state: SignInState): string | null {
  return 'identifier' in state ? state.identifier : null;
}

export function classifySignInError(err: unknown): 'UNCONFIRMED' | 'RESET_REQUIRED' | null {
  const name = (err as { name?: string } | null)?.name;
  if (name === 'UserNotConfirmedException') return 'UNCONFIRMED';
  if (name === 'PasswordResetRequiredException') return 'RESET_REQUIRED';
  return null;
}

export function toMessage(err: unknown, fallback = 'Something went wrong.'): string {
  return extractErrorMessage(err, fallback);
}
